import React, { useState, useEffect, useCallback, forwardRef } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { withStyles } from "@material-ui/core/styles";
import { styled } from "@mui/material/styles";
import Popper from "@mui/material/Popper";

const CssTextField = withStyles({
  root: {
    "& label.Mui-focused": {
      color: "#ABADB3",
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "green",
    },
    "& .MuiOutlinedInput-root": {
      height: "26px",
      padding: "0px",
      backgroundColor: "white !important",
      borderRadius: "2px",
      fontFamily: `"Malgun Gothic", "맑은 고딕", AppleSDGothicNeo-Light, sans-serif`,
      "& fieldset": { color: "#ABADB3" },
      "&.Mui-focused fieldset": {
        borderColor: "rgba(0, 176, 246, 1)",
      },
    },
    "& .MuiAutocomplete-input": {
      padding: "1.5px 4px 2.5px 6px !important",
    },
    "& .MuiSvgIcon-fontSizeSmall": {
      width: "0.95rem",
      height: "0.95rem",
      color: "#4A4A4A !important",
    },
    "& .MuiAutocomplete-endAdornment": {
      right: "2px !important",
      top: "2px !important",
    },
  },
})(TextField);

const DUMMY_LIST = [{ CODE_NM: "", CODE_CD: "" }];

const AutoCompleteInputTextField = forwardRef((props, ref) => {
  const [lists, setLists] = useState(props.lists ?? DUMMY_LIST);
  const [value, setValue] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [open, setOpen] = useState(false);

  const StyledPopper = styled(Popper)({
    "& .MuiAutocomplete-paper": {
      backgroundColor: "white",
      width: props.paperWidth ? props.paperWidth : "100%",
    },
    "& .MuiAutocomplete-listbox": {
      padding: "0",
      "& .MuiAutocomplete-option": {
        padding: "8px 16px",
        fontSize: "13px",
        "&[data-focus='true']": {
          backgroundColor: "rgba(224, 224, 224, 1) !important", // Highlight color for focused
        },
        "&:hover": {
          backgroundColor: "rgba(224, 224, 224, 1) !important", // Highlight color for hover
        },
        "&[data-focus='true']:hover, &.Mui-focused": {
          backgroundColor: "rgba(224, 224, 224, 1) !important", // Same color for focused and hover
        },
        "&[data-focus='true']:not(:hover), &:not(.Mui-focused)": {
          backgroundColor: "transparent !important", // Remove hover color if not focused
        },
      },
    },
  });

  const handleChange = (event, newValue) => {
    setValue(newValue);
    if (newValue) {
      setOpen(false);
    }
  };

  const handleInputChange = (event, newInputValue) => {
    let _val = newInputValue.toUpperCase();
    setInputValue(_val);
    setOpen(newInputValue.length > 0);
    if (!lists.find((item) => item.CODE_NM === _val)) {
      setValue(null);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !open) {
      const focusableElements = document.querySelectorAll(
        "input, textarea, .MuiSelect-select"
      );

      const activeElement = document.activeElement;

      const currentIndex = Array.prototype.indexOf.call(
        focusableElements,
        activeElement
      );

      let nextElement;

      if (event.shiftKey) {
        nextElement =
          focusableElements[currentIndex - 1] || focusableElements[0];
      } else {
        nextElement =
          focusableElements[currentIndex + 1] || focusableElements[0];
      }

      nextElement?.focus();
    }
  };

  const getVal = useCallback(() => {
    return {
      code: value?.CODE_CD ?? "",
      name: value?.CODE_NM ?? "",
      input: inputValue,
    };
  }, [value, inputValue]);

  useEffect(() => {
    if (ref) {
      ref.current = {
        get: getVal,
        set: setValue,
      };
    }
  }, [getVal, ref]);

  useEffect(() => {
    if (lists.length === 1) {
      setValue(lists[0]);
    }
  }, [lists]);

  return (
    <Autocomplete
      freeSolo
      readOnly={props.readOnly ?? false}
      autoHighlight={false}
      openOnFocus={false}
      options={lists}
      open={open}
      getOptionLabel={(it) => it.CODE_NM ?? it}
      getOptionSelected={(option, value) => option.CODE_CD === value.CODE_CD}
      sx={{
        width: props.width ?? 190,
        backgroundColor: "white",
        display: props.display ?? "block",
      }}
      PopperComponent={(props) => (
        <StyledPopper {...props} sx={{ zIndex: 2000 }} />
      )}
      renderOption={(props, it) => (
        <li
          {...props}
          key={it.CODE_CD}
          style={{
            fontSize: "13px",
            padding: "6px 4px",
          }}
        >
          {it.CODE_NM}
        </li>
      )}
      renderInput={(params) => (
        <CssTextField
          {...params}
          placeholder={props.placeholder ?? ""}
          styles={{
            textAlign: "right",
          }}
          size="small"
          onDoubleClick={() => {
            setOpen(true);
          }}
          onBlur={() => {
            setOpen(false);
          }}
          onFocus={(event) => event.target.select()}
          onKeyDown={handleKeyDown}
          InputProps={{
            ...params.InputProps,
            style: {
              right: "0px",
              fontSize: "13px",
              height: "26px",
              padding: "0px",
            },
          }}
          inputProps={{
            ...params.inputProps,
            autoComplete: "one-time-code",
            style: {
              textAlign: props.textAlign ? props.textAlign : "left",
            },
          }}
        />
      )}
      value={value}
      inputValue={inputValue}
      onChange={handleChange}
      onInputChange={handleInputChange}
    />
  );
});

export default AutoCompleteInputTextField;
